/**
 * Pure helpers for the resume gap analysis section on the CV page.
 *
 * Every count, date and severity comes from the server, which counts them by
 * rules; these only say them the same way everywhere. Dates arrive as
 * `YYYY-MM-DD` (the first of a month) and are read as calendar dates, never
 * through `Date`, so a time zone can never move "Jun 2019" to "May 2019".
 */

import { paths } from '@/routes/paths';

export const SEVERITIES = {
  important: { label: 'Important', tone: 'warning', rank: 0 },
  suggested: { label: 'Suggested', tone: 'info', rank: 1 },
};

/** Label and badge tone for a severity; unknown values read as suggested. */
export function severityOf(severity) {
  return SEVERITIES[severity] ?? SEVERITIES.suggested;
}

/** Important first, then suggested; the server's order within each. */
export function sortBySeverity(items) {
  return (items ?? [])
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        severityOf(a.item.severity).rank - severityOf(b.item.severity).rank || a.index - b.index,
    )
    .map(({ item }) => item);
}

/** { important, suggested } counted from a list of items. */
export function countBySeverity(items) {
  const counts = { important: 0, suggested: 0 };
  for (const item of items ?? []) {
    counts[item.severity === 'important' ? 'important' : 'suggested'] += 1;
  }
  return counts;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2019-06-01" -> "Jun 2019". '' for anything that is not a date. */
export function monthYear(value) {
  const match = /^(\d{4})-(\d{2})/.exec(value ?? '');
  if (!match) return '';
  const month = Number(match[2]);
  if (month < 1 || month > 12) return '';
  return `${MONTHS[month - 1]} ${match[1]}`;
}

/** 9 -> "9 months", 12 -> "1 year", 15 -> "1 year 3 months". */
export function durationLabel(months) {
  const total = Math.max(0, Math.round(Number(months) || 0));
  const years = Math.floor(total / 12);
  const rest = total % 12;
  const parts = [];
  if (years) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (rest || !years) parts.push(`${rest} month${rest === 1 ? '' : 's'}`);
  return parts.join(' ');
}

/** "Jun 2019 – Mar 2020", or "Jun 2025 – Present" for a gap running to today. */
export function gapRange(gap) {
  const end = gap.ongoing ? 'Present' : monthYear(gap.end);
  return `${monthYear(gap.start)} – ${end}`;
}

/** "Jun 2019 – Mar 2020 · 9 months" */
export function formatGap(gap) {
  return `${gapRange(gap)} · ${durationLabel(gap.months)}`;
}

/** What sits either side of a gap, in words. */
export function gapContext(gap) {
  if (gap.after && gap.before) return `Between ${gap.after} and ${gap.before}`;
  if (gap.after && gap.ongoing) return `Since ${gap.after}`;
  if (gap.after) return `After ${gap.after}`;
  if (gap.before) return `Before ${gap.before}`;
  return '';
}

export const MISMATCH_KINDS = {
  skill: { label: 'Skills', icon: 'zap' },
  education: { label: 'Education', icon: 'graduationCap' },
  experience: { label: 'Experience', icon: 'briefcase' },
};

/** Items grouped by kind, in a fixed order, skipping empty groups. */
export function groupByKind(items) {
  return Object.entries(MISMATCH_KINDS)
    .map(([kind, meta]) => ({
      kind,
      ...meta,
      items: (items ?? []).filter((item) => item.kind === kind),
    }))
    .filter((group) => group.items.length > 0);
}

/** The one-line summary under the header's counts. */
export function summarySentence(summary) {
  if (!summary || summary.total === 0) {
    return 'No gaps found. This CV covers everything these checks look for.';
  }
  const parts = [];
  if (summary.important) parts.push(`${summary.important} important`);
  if (summary.suggested) parts.push(`${summary.suggested} suggested`);
  return `${summary.total} thing${summary.total === 1 ? '' : 's'} to look at: ${parts.join(', ')}. Start with the important ones.`;
}

/** Where "Check my CV against this goal" lives on Career guidance. */
export const CV_CHECK_LINK = `${paths.student.career}#cv-check`;

/** The four parts, in order: what each is called and how it reads at a glance. */
export const GAP_PARTS = [
  {
    key: 'skills',
    icon: 'target',
    title: 'Skill gaps',
    status: (part) =>
      part.status === 'no_goal'
        ? 'No career goal set yet'
        : part.status === 'no_demand'
          ? 'No listing in this field names a skill yet'
          : part.missing.length
            ? `${part.missing.length} missing skill${part.missing.length === 1 ? '' : 's'} your field asks for`
            : 'No skill gaps found',
    // A missing goal is not "all clear": it needs the student to act.
    needsAttention: (part) => part.status === 'no_goal' || part.missing.length > 0,
  },
  {
    key: 'timeline',
    icon: 'calendar',
    title: 'Timeline gaps',
    status: (part) =>
      part.status === 'no_dates'
        ? 'No dates we could read on this CV'
        : part.gaps.length
          ? `${part.gaps.length} break${part.gaps.length === 1 ? '' : 's'} of more than ${part.threshold_months} months`
          : 'No timeline gaps found',
    needsAttention: (part) => part.status === 'no_dates' || part.gaps.length > 0,
  },
  {
    key: 'sections',
    icon: 'fileText',
    title: 'Missing or weak sections',
    status: (part) =>
      part.items.length
        ? `${part.passed} of ${part.checked} checks passed`
        : 'No missing or weak sections',
    needsAttention: (part) => part.items.length > 0,
  },
  {
    key: 'profile',
    icon: 'userCheck',
    title: 'CV vs profile differences',
    status: (part) => {
      const count = part.cv_only.length + part.profile_only.length;
      return count
        ? `${count} difference${count === 1 ? '' : 's'} between this CV and your profile`
        : 'Your CV and profile match';
    },
    needsAttention: (part) => part.cv_only.length + part.profile_only.length > 0,
  },
];
